import { useTheme } from '../../context/ThemeContext';

// Icons
const TrophyIcon = () => (
  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6" />
    <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18" />
    <path d="M4 22h16" />
    <path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22" />
    <path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22" />
    <path d="M18 2H6v7a6 6 0 0 0 12 0V2Z" />
  </svg>
);

const MapIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="3 6 9 3 15 6 21 3 21 18 15 21 9 18 3 21" />
    <line x1="9" y1="3" x2="9" y2="18" />
    <line x1="15" y1="6" x2="15" y2="21" />
  </svg>
);

const GlobeIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <line x1="2" y1="12" x2="22" y2="12" />
    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
  </svg>
);

const TrendingUpIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
    <polyline points="17 6 23 6 23 12" />
  </svg>
);

const StarIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
  </svg>
);

const AwardIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="8" r="6" />
    <path d="M15.477 12.89 17 22l-5-3-5 3 1.523-9.11" />
  </svg>
);

const LocationScoreboard = ({ location }) => {
  const { theme } = useTheme();

  const leaderboardTypes = [
    {
      icon: <MapIcon />,
      title: 'Local Rankings',
      description: 'See how you rank against shops in your city/area',
      color: '#3b82f6',
    },
    {
      icon: <GlobeIcon />,
      title: 'State Rankings',
      description: 'Compare with all shops in your state',
      color: '#22c55e',
    },
    {
      icon: <TrophyIcon style={{ width: 20, height: 20 }} />,
      title: 'National Rankings',
      description: 'Compete with top shops across the country',
      color: '#f59e0b',
    },
  ];

  const metrics = [
    {
      icon: <TrendingUpIcon />,
      title: 'Total Trades Hosted',
      description: 'Number of completed trades at your location',
      color: '#8b5cf6',
    },
    {
      icon: <StarIcon />,
      title: 'Average Rating',
      description: 'Your shop\'s rating from trader reviews',
      color: '#f59e0b',
    },
    {
      icon: <AwardIcon />,
      title: 'Trader Satisfaction',
      description: 'Repeat visitors and recommendation rate',
      color: '#ec4899',
    },
  ];

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={{ ...styles.header, backgroundColor: theme.bgSheet, borderColor: theme.border }}>
        <div style={{ ...styles.iconContainer, backgroundColor: 'rgba(245, 158, 11, 0.15)' }}>
          <TrophyIcon />
        </div>
        <div style={styles.headerText}>
          <h2 style={{ ...styles.title, color: theme.text }}>Scoreboard</h2>
          <p style={{ ...styles.subtitle, color: theme.textSecondary }}>
            See how your shop ranks against others
          </p>
        </div>
      </div>

      {/* Coming Soon Banner */}
      <div style={{ ...styles.comingSoonBanner, backgroundColor: 'rgba(245, 158, 11, 0.1)', borderColor: 'rgba(245, 158, 11, 0.3)' }}>
        <span style={{ ...styles.comingSoonText, color: '#f59e0b' }}>Coming Soon</span>
        <p style={{ ...styles.comingSoonDesc, color: theme.textSecondary }}>
          Rankings and leaderboards are being developed!
        </p>
      </div>

      {/* Placeholder Ranking */}
      <div style={{ ...styles.rankPreview, backgroundColor: theme.bgSheet, borderColor: theme.border }}>
        <div style={styles.rankNumber}>
          <span style={styles.rankHash}>#</span>
          <span style={styles.rankValue}>--</span>
        </div>
        <div style={styles.rankInfo}>
          <span style={{ ...styles.shopName, color: theme.text }}>{location?.store_name || 'Your Shop'}</span>
          <span style={{ ...styles.rankLabel, color: theme.textSecondary }}>Ranking not yet available</span>
        </div>
      </div>

      {/* Leaderboard Types */}
      <div style={styles.section}>
        <h3 style={{ ...styles.sectionTitle, color: theme.text }}>Leaderboard Categories</h3>
        {leaderboardTypes.map((type, index) => (
          <div
            key={index}
            style={{
              ...styles.featureCard,
              backgroundColor: theme.bgSheet,
              borderColor: theme.border,
              opacity: 0.7,
            }}
          >
            <div style={{ ...styles.featureIcon, backgroundColor: `${type.color}20`, color: type.color }}>
              {type.icon}
            </div>
            <div style={styles.featureContent}>
              <span style={{ ...styles.featureTitle, color: theme.text }}>{type.title}</span>
              <span style={{ ...styles.featureDesc, color: theme.textSecondary }}>{type.description}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Metrics */}
      <div style={styles.section}>
        <h3 style={{ ...styles.sectionTitle, color: theme.text }}>Ranking Metrics</h3>
        {metrics.map((metric, index) => (
          <div
            key={index}
            style={{
              ...styles.featureCard,
              backgroundColor: theme.bgSheet,
              borderColor: theme.border,
              opacity: 0.7,
            }}
          >
            <div style={{ ...styles.featureIcon, backgroundColor: `${metric.color}20`, color: metric.color }}>
              {metric.icon}
            </div>
            <div style={styles.featureContent}>
              <span style={{ ...styles.featureTitle, color: theme.text }}>{metric.title}</span>
              <span style={{ ...styles.featureDesc, color: theme.textSecondary }}>{metric.description}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Info Box */}
      <div style={{ ...styles.infoBox, backgroundColor: theme.bgSheet, borderColor: theme.border }}>
        <p style={{ ...styles.infoText, color: theme.textSecondary }}>
          Rankings are calculated based on trade volume, trader ratings, and
          community engagement. Climb the leaderboard by hosting more trades
          and providing great experiences!
        </p>
      </div>
    </div>
  );
};

const styles = {
  container: {
    padding: 16,
    paddingBottom: 120,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    padding: 20,
    borderRadius: 16,
    border: '1px solid',
    marginBottom: 16,
  },
  iconContainer: {
    width: 64,
    height: 64,
    borderRadius: 16,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#f59e0b',
  },
  headerText: {
    flex: 1,
  },
  title: {
    fontSize: 20,
    fontWeight: 700,
    margin: 0,
  },
  subtitle: {
    fontSize: 14,
    margin: '4px 0 0 0',
  },
  comingSoonBanner: {
    padding: 16,
    borderRadius: 12,
    border: '1px solid',
    marginBottom: 20,
    textAlign: 'center',
  },
  comingSoonText: {
    fontSize: 14,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: 1,
  },
  comingSoonDesc: {
    fontSize: 13,
    margin: '8px 0 0 0',
  },
  rankPreview: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    padding: 20,
    borderRadius: 16,
    border: '1px solid',
    marginBottom: 20,
  },
  rankNumber: {
    display: 'flex',
    alignItems: 'baseline',
  },
  rankHash: {
    fontSize: 24,
    fontWeight: 300,
    color: '#6a6a6a',
  },
  rankValue: {
    fontSize: 48,
    fontWeight: 800,
    color: '#f59e0b',
  },
  rankInfo: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  shopName: {
    fontSize: 16,
    fontWeight: 600,
  },
  rankLabel: {
    fontSize: 13,
  },
  section: {
    marginBottom: 20,
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: 600,
    margin: '0 0 12px 0',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  featureCard: {
    display: 'flex',
    alignItems: 'center',
    gap: 14,
    padding: 14,
    borderRadius: 12,
    border: '1px solid',
    marginBottom: 8,
  },
  featureIcon: {
    width: 44,
    height: 44,
    borderRadius: 10,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  featureContent: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },
  featureTitle: {
    fontSize: 14,
    fontWeight: 600,
  },
  featureDesc: {
    fontSize: 12,
  },
  infoBox: {
    padding: 16,
    borderRadius: 12,
    border: '1px solid',
  },
  infoText: {
    fontSize: 13,
    lineHeight: 1.5,
    margin: 0,
  },
};

export default LocationScoreboard;
